/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import { Row, Container, Col, Form, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import * as Yup from 'yup';
import styles from './OwnerRegistration.module.css';
import { Credentials } from 'model/Credentials';
import Message from '../../containers/common/Message';
import { FloButton, FloFormGroup, FloFormControl, FloDropdown, RedirectType, Redirect } from 'components';
import { useParams, Link } from 'react-router-dom';
import classnames from 'classnames';
import { User, InvitationData } from 'model';
import { isEmpty } from 'lodash';
import { PhoneNumber } from 'model/PhoneNumber';
import queryString from 'query-string';

type OwnerRegistrationProps = {
  validateRegistrationToken: (token: string) => void,
  acceptInvitation: (user: User, credentials: Credentials, token: string) => void,
  isFetching: boolean,
  hasError: boolean,
  isSubmitting: boolean,
  invitationData: InvitationData,
  verifiedOK: boolean,
}

const OwnerRegistration: React.FC<OwnerRegistrationProps> = React.memo(({
  validateRegistrationToken,
  isFetching,
  hasError,
  isSubmitting,
  acceptInvitation,
  invitationData,
  verifiedOK,
}) => {

  const { t, i18n } = useTranslation();
  const urlParams = useParams<{ token?: string }>();

  // eslint-disable-next-line no-restricted-globals
  const queryParams = queryString.parse(location.search);
  const token = queryParams.token ? queryParams.token as string : urlParams.token;
  useEffect(() => {
    validateRegistrationToken(token || '')
  }, []);

  useEffect(() => {
    i18n.changeLanguage(invitationData.metadata.locale);
  }, [invitationData.metadata.locale]);

  // eslint-disable-next-line no-useless-escape
  const phoneRegExp = /^(?:(?:\(?(?:00|\+)([1-4]\d\d|[1-9]\d?)\)?)?[\-\.\ \\\/]?)?((?:\(?\d{1,}\)?[\-\.\ \\\/]?){0,})(?:[\-\.\ \\\/]?(?:#|ext\.?|extension|x)[\-\.\ \\\/]?(\d+))?$/i

  return (
    <Container>
      <Row>
        <Col className={styles.logo} >
          <img alt="Flo Multi-Unit Dashboard" src="/images/MoenSmartLogo.png" />
        </Col>
      </Row>
      <Row>
        <Col className={styles.title}>
          {t('Account Registration')}
        </Col>
      </Row>
      <Row
        className={classnames(
          styles.root,
          { [styles.isLoading]: isFetching }
        )}
      >
        { isFetching &&
          <Col xs="auto" >
            <Spinner className={styles.loader} animation="border" role="status">
              <span className="sr-only">Loading...</span>
            </Spinner>
          </Col>
        }
        { !isFetching &&
          <Col
            className={classnames(
              styles.formWrapper,
              { [styles.expirationError]: ((hasError && isEmpty(invitationData.token)) || verifiedOK)}
            )}
            xs="12"
            md={{ span: 6, offset: 3 }}
          >
            <Message />

            { verifiedOK && (
              <div>
                <div className={styles.loginRedirect}><Redirect type={RedirectType.GUIDE} timeoutSeconds={2} message={t('redirect-link-below')}/></div>
                <div className={styles.loginRedirect}><Link to="/login">{t('go_to_login')}</Link></div>
              </div>
            )}

            { (!hasError || !isEmpty(invitationData.token)) && !verifiedOK &&
            <Formik
              enableReinitialize
              initialValues={{
                countryCode: '+1',
                firstName: '',
                lastName: '',
                email: invitationData.metadata.email,
                password: '',
                passwordConfirmation: '',
                phone: '',
                locale: invitationData.metadata.locale
              }}
              onSubmit={(values, actions) => {
                const phone = `${values.countryCode}${values.phone}`;
                acceptInvitation({
                  firstName: values.firstName,
                  lastName: values.lastName,
                  phoneMobile: phone,
                  email: values.email,
                  locale: values.locale
                }, {
                  password: values.password,
                  username: values.email
                }, invitationData.token)
                actions.setSubmitting(false);
              }}
              validationSchema={Yup.object().shape({
                firstName: Yup.string().required(t('validation_required', { field: t('First Name') })),
                lastName: Yup.string().required(t('validation_required', { field: t('Last Name') })),
                email: Yup.string().required(t('validation_required', { field: t('Email') })),
                countryCode: Yup.string().required(t('validation_required', { field: t('Country Code') })),
                phone: Yup.string().matches(phoneRegExp, t('validation_phone_invalid')).required(t('validation_required', { field: t('Phone') })),
                locale: Yup.string().required(t('validation_required', { field: t('Locale') })),
                password: Yup.string().required(t('validation_required', { field: t('Password') }))
                  .min(8, t('min_password'))
                  .matches(/[a-z]/, t('lowercase_required'))
                  .matches(/[A-Z]/, t('uppercase_required'))
                  .matches(/[0-9]/, t('number_required'))
                  .matches(/[!@#$%^&*)(+=._-]/, t('special_char_required')),
                passwordConfirmation: Yup.string().when("password", {
                  is: val => (val && val.length > 0 ? true : false),
                  then: Yup.string().oneOf(
                    [Yup.ref("password")], t('password_not_match'))
                }).required(t('validation_required', { field: t('Confirm Password') })),
              })}
              render={({ values, handleBlur, handleChange, errors, touched, isValid, handleSubmit, setFieldValue, setFieldTouched }) => {
                const setField = (name: string, value: any) => {
                  setFieldValue(name, value, true);
                  setFieldTouched(name, true, false);
                };
                return (
                  <Form className={styles.form} onSubmit={handleSubmit}>
                    <Row>
                      <Col xs="12">
                        <FloFormGroup
                          controlId   = "firstName"
                          label       = {t('First Name')}
                          touched     = {touched}
                          errors      = {errors}
                        >
                          <FloFormControl
                            type="text"
                            name="firstName"
                            placeholder={t('First Name')}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.firstName}
                            disabled={isSubmitting}
                            isInvalid={!!(touched.firstName && errors.firstName)}
                          />
                        </FloFormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col xs="12">
                        <FloFormGroup
                          controlId   = "lastName"
                          label       = {t('Last Name')}
                          touched     = {touched}
                          errors      = {errors}
                        >
                          <FloFormControl
                            type="text"
                            name="lastName"
                            placeholder={t('Last Name')}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.lastName}
                            disabled={isSubmitting}
                            isInvalid={!!(touched.lastName && errors.lastName)}
                          />
                        </FloFormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col xs="12">
                        <FloFormGroup
                          controlId   = "email"
                          label       = {t('Email')}
                          touched     = {touched}
                          errors      = {errors}
                        >
                          <FloFormControl
                            type="email"
                            name="email"
                            placeholder={t('Email')}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.email}
                            disabled
                            isInvalid={!!(touched.email && errors.email)}
                          />
                        </FloFormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col xs="5">
                        <FloFormGroup
                          controlId   = "countryCode"
                          label       = {t('Country Code')}
                          touched     = {touched}
                          errors      = {errors}
                        >
                          <FloDropdown
                            id      = "countryCode"
                            title   = {t('Country Code')}
                            values  = {values.countryCode}
                            full
                            disabled = {isSubmitting}
                            options = {PhoneNumber.countryCodes}
                            onChange = {e => setField('countryCode', e.value)}
                            isInvalid={!!(touched.countryCode && errors.countryCode)}
                          />
                        </FloFormGroup>
                      </Col>
                      <Col xs="7">
                        <FloFormGroup
                          controlId   = "phone"
                          label       = {t('Phone')}
                          touched     = {touched}
                          errors      = {errors}
                        >
                          <FloFormControl
                            type="text"
                            name="phone"
                            placeholder={t('Phone')}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.phone}
                            disabled={isSubmitting}
                            isInvalid={!!(touched.phone && errors.phone)}
                          />
                        </FloFormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col xs="12">
                        <FloFormGroup
                          controlId   = "locale"
                          label       = {t('Locale')}
                          touched     = {touched}
                          errors      = {errors}
                        >
                          <FloDropdown
                            id      = "locale"
                            title   = {t('Locale')}
                            values  = {values.locale}
                            full
                            disabled = {isSubmitting}
                            options = {[
                              { value: 'en-us', label: 'English' },
                              { value: 'fr-ca', label: 'French' },
                            ]}
                            onChange = {e => setField('locale', e.value)}
                            isInvalid={!!(touched.locale && errors.locale)}
                          />
                        </FloFormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col xs="12">
                        <FloFormGroup
                          controlId   = "password"
                          label       = {t('Password')}
                          touched     = {touched}
                          errors      = {errors}
                        >
                          <FloFormControl
                            type="password"
                            name="password"
                            placeholder={t('Password')}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.password}
                            disabled={isSubmitting}
                            isInvalid={!!(touched.password && errors.password)}
                          />
                        </FloFormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col xs="12">
                        <FloFormGroup
                          controlId   = "passwordConfirmation"
                          label       = {t('Confirm Password')}
                          touched     = {touched}
                          errors      = {errors}
                        >
                          <FloFormControl
                            type="password"
                            name="passwordConfirmation"
                            placeholder={t('Confirm Password')}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.passwordConfirmation}
                            disabled={isSubmitting}
                            isInvalid={!!(touched.passwordConfirmation && errors.passwordConfirmation)}
                          />
                        </FloFormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col xs="12">
                        <FloButton
                          className   ={styles.submitButton}
                          text        = { t('registration_confirm_button') }
                          onClick     = {() => {}}
                          disabled    = {!isValid || isSubmitting}
                          showLoader  = {isSubmitting}
                          fullWidth
                          submit
                        />
                      </Col>
                    </Row>

                  </Form>
                )
              }}
            /> }
          </Col>
        }
      </Row>
    </Container>
  );
});

export default OwnerRegistration;
